import { type Descendant, Element, Text } from 'slate';

/**
 * When deserializing a value, we can end up with a valid value that
 * is hard to normalize. For example, Slate will merge consecutive
 * text nodes, of which there are usually a lot when coming from HTML.
 *
 * Slate normalization on large, invalid documents can be very slow
 * and memory-intensive. Slate will call Change methods, which
 * generate operations, which affect the value. Each operation
 * includes the entire Slate Value that it operates on. And after
 * applying an operation, Slate needs to do several
 * depth-first-searches of the entire tree to generate paths.
 *
 * If we can give Slate a mostly-normalized document, we can skip all
 * that work. So this method does some of the default Slate
 * normalizations on the JavaScript object representation of the
 * Value, in order to prevent normalization on the Value itself later
 * on.
 */
function normalizeValueJSON(nodes: Descendant[]) {
  // We can't do json-based normalization if we have annotations,
  // because by merging text nodes, we will mess up the paths where
  // the annotation points to.
  //
  // I don't think this will be a problem, because we only saw
  // performance problems here when converting old tinymce documents,
  // which didn't have annotations. If this does become a problem, we
  // will have to rewrite this to transform annotation anchor / focus
  // as we adjust nodes in this function.
  return normalizeNodes(nodes);
}

function normalizeNodes(nodes: Descendant[]) {
  nodes = mergeTextChildren(nodes);
  nodes.forEach((node) => {
    if (Element.isElement(node) && node.children)
      node.children = normalizeNodes(node.children);
  });

  return nodes;
}

/**
 * Merges node and prevNode into a single text node as long as marks
 * match.
 */
function mergeTextNode(prevNode: Text, node: Text) {
  if (prevNode && Text.equals(prevNode, node, { loose: true })) {
    prevNode.text += node.text;
    return [prevNode];
  } else {
    return [prevNode, node];
  }
}

/**
 * Merges every run of text children of a node into as few text nodes
 * as possible.
 */
function mergeTextChildren(nodes: Descendant[]) {
  return nodes.reduce<Descendant[]>((acc, child) => {
    const previousChild = acc[acc.length - 1];
    if (!Text.isText(child)) {
      return acc.concat(child);
    } else if (!previousChild || !Text.isText(previousChild)) {
      return acc.concat(child);
    } else {
      const results = mergeTextNode(previousChild, child);
      return acc.slice(0, -1).concat(results);
    }
  }, []);
}

export default normalizeValueJSON;
